import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import {
  ButtonGroup,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";

type Props = {
  currentZoom: number;
  increment: number;
  onClickPlus: (newZoomLevel: number) => void;
  onClickMinus: (newZoomLevel: number) => void;
  onClickReset?: () => void;
  showResetButton?: boolean;
  position?: 'fixed' | 'absolute';
  bottom?: number | string;
  right?: number | string;
};

export const ZoomControl: React.FC<Props> = ({
  increment = 0.1,
  onClickPlus,
  onClickMinus,
  onClickReset,
  showResetButton = false,
  position = 'fixed',
  bottom,
  right,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [zoomChange, setZoomChange] = React.useState<number>(0);
  const [, setShowZoomLevel] = React.useState<boolean>(false);

  React.useEffect(() => {
    // Show the zoom level for 2 seconds
    setShowZoomLevel(true);
    const timer = setTimeout(() => {
      setShowZoomLevel(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, [zoomChange]);

  return (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="flex-start"
      spacing={1}
      sx={{
        position: position,
        bottom: bottom ?? theme.spacing(2),
        right: right ?? theme.spacing(2),
        // Must be below MUI dialogs (z-index: 1300)
        zIndex: 1100,
      }}
    >
      <ButtonGroup
        orientation="vertical"
        variant="contained"
        sx={{ backgroundColor: "white" }}
      >
        <Tooltip title={t('widgets.zoomControl.zoomIn')} placement="left">
          <IconButton
            onClick={() => {
              const newZoomLevel = 1 + (zoomChange + 1) * increment;
              onClickPlus(newZoomLevel);
              setZoomChange(zoomChange + 1);
            }}
            sx={{
              borderRadius: 0,
              color: "white",
              backgroundColor: "success.main",
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
        <Divider />
        <Tooltip title={t('widgets.zoomControl.zoomOut')} placement="left">
          <IconButton
            onClick={() => {
              const newZoomLevel = 1 + (zoomChange - 1) * increment;
              if (newZoomLevel <= 0) {
                return;
              }
              onClickMinus(newZoomLevel);
              setZoomChange(zoomChange - 1);
            }}
            sx={{
              borderRadius: 0,
              color: "white",
              backgroundColor: "success.main",
            }}
          >
            <RemoveIcon />
          </IconButton>
        </Tooltip>
        {showResetButton && onClickReset && (
          <>
            <Divider />
            <Tooltip title={t('widgets.zoomControl.resetZoom')} placement="left">
              <IconButton
                onClick={() => {
                  onClickReset();
                  setZoomChange(0);
                }}
                sx={{
                  borderRadius: 0,
                  color: "white",
                  backgroundColor: "success.main",
                }}
              >
                <CenterFocusStrongIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </ButtonGroup>
    </Stack>
  );
};
